import React, { useEffect } from 'react';

import { useDispatch } from 'react-redux';


import SubmitModal from 'containers/modals/SubmitModal';
import EndQuizBlockModal from 'containers/modals/EndQuizBlockModal';
import Button from 'components/inputs/Button';
import LoadingIcon from 'components/LoadingIcon';
import FredEndQuizBlockModal from 'components/TakeQuizPage/Fred/EndQuizBlockModal';
import PearsonEndQuizBlockModal from 'components/TakeQuizPage/PearsonVue/EndQuizBlockModal';
import PearsonVueConfirmationModal from 'containers/TakeQuizPage/modals/PearsonVueConfirmationModal';
import { Modal as ModalActions, Questions, Quizzes } from 'actions/sharedJsActions';

import useNavigation from './navigation';

const { modalOpen, modalClose } = ModalActions;
const { questionResultsFetch } = Questions;
const {
  quizSubmit,
  quizEndQuizBlock,
  quizQuestionFetchNext,
  quizSetLastSeenBlock
} = Quizzes;

export const useQuizNavigation = (
  quiz,
  questions,
  filter,
  quizId,
  questionId,
  questionNumber,
  syncResponse,
  syncTimeSpent,
  skipExitSyncRef,
  maxQuestionNumber,
  quizRef,
  setShowResponseCorrectness,
  setShowExplanation,
) => {
  const {
    browse, navigateQuiz, pause, report
  } = useNavigation();
  const dispatch = useDispatch();

  const getQuestionsThenNavigate = (lowBnd, highBnd, newQuestionNumber) => {
    const questionIds = quiz.get('question_ids');
    dispatch(questionResultsFetch(questionIds.slice(lowBnd, highBnd)))
      .then(() => browse(newQuestionNumber))
      .catch(error => console.warn(error));
  };

  const goToQuestion = (newQuestionNumber) => {
    setShowResponseCorrectness(0);
    setShowExplanation(false);
    if (quiz.get('type') === 'search') {
      const newQuestionIdx = newQuestionNumber - 1;
      const newQuestionId = quiz.get('question_ids').get(newQuestionIdx);
      const hasFullQuestion = !!questions.get(newQuestionId).get('safe_name');

      if (!hasFullQuestion) {
        if (newQuestionNumber < questionNumber) {
          const lowerBound = newQuestionIdx - 10 > 0 ? newQuestionIdx - 10 : 0;
          return getQuestionsThenNavigate(lowerBound, newQuestionNumber, newQuestionNumber);
        }
        return getQuestionsThenNavigate(newQuestionIdx, newQuestionIdx + 10, newQuestionNumber);
      }
      browse(newQuestionNumber);
    } else {
      navigateQuiz(quizId, newQuestionNumber, filter === "specific" ? "questions" : filter);
    }
  };

  const goToQuestionInBlock = (newQuestionNumber) => {
    const newQuestionId = quiz.get('currentBlockQuestionIds').get(newQuestionNumber - 1);
    const quizQuestionIndex = quiz.get('question_ids').findIndex(qId => qId === newQuestionId);
    return goToQuestion(quizQuestionIndex + 1);
  };

  const seenCaseQuestionIds = quiz.get('seen_case_question_ids');
  const hasBeenSeen = seenQuestionId => !!seenCaseQuestionIds.find(id => id === seenQuestionId);

  const goToLastSeenQuestionInGroup = (questionGroup) => {
    if (!seenCaseQuestionIds.size) return goToQuestion(quiz.get('question_ids').indexOf(questionGroup.get(0)) + 1);

    for (const [index, id] of questionGroup.reverse().entries()) {
      if (index === questionGroup.size - 1 || hasBeenSeen(id)) {
        return goToQuestion(quiz.get('question_ids').indexOf(id) + 1);
      }
    }
  };

  const onClickQuestionNav = async (newQuestionNumber) => {
    if (quiz.get('currentStatus') !== 'complete') {
      if (quiz.get('type') === 'adaptive') {
        await syncResponse({ navigatingAway: true, isComplete: quiz.get('currentStatus') === 'complete' });
      } else {
        syncTimeSpent(quiz.get('currentStatus') === 'complete');
      }
    }
    if (['search', 'study'].includes(quiz.get('type'))
      || ((quiz.get('type') === 'test') && quiz.get('is_ngn'))) return goToQuestion(newQuestionNumber);
    if (quiz.get('isBlockQuiz')) return goToQuestionInBlock(newQuestionNumber);
    // Check to make sure that target question isn't part of a group
    const newQuestionId = quiz.getIn(['question_ids', newQuestionNumber - 1]);
    const newQuestion = questions.get(newQuestionId);
    const newQuestionGroup = newQuestion.get('group_question_ids');
    const newQuestionIndex = newQuestionGroup.indexOf(newQuestionId);

    if (!newQuestionGroup.size || !newQuestionIndex) return goToQuestion(newQuestionNumber);

    const prevQuestionId = newQuestionGroup.get(newQuestionIndex - 1);
    if (hasBeenSeen(newQuestionId) || hasBeenSeen(prevQuestionId) || quiz.get('currentStatus') === 'complete') {
      return goToQuestion(newQuestionNumber);
    }
    return goToLastSeenQuestionInGroup(newQuestionGroup);
  };

  // / reg quiz nav
  const onFinishSubmit = () => {
    skipExitSyncRef.current = true;
    dispatch(quizSubmit(quizId));
    dispatch(modalClose());
    report(quizId);
  };

  // TODO: Add a `canClickSubmit` boolean to use instead of setting `onClickSubmit = null`.
  // TODO: `canClickSubmit` should be false if any sync requests (response, seen question, tick, etc.) are still loading
  const onClickSubmit = quiz.get('currentStatus') !== 'incomplete' ? null : async () => {
    if (quiz.get('boardExamInterface') === 'psi') return onFinishSubmit();

    dispatch(modalOpen(<LoadingIcon>Syncing...</LoadingIcon>, { closable: false }));
    await syncResponse({ isComplete: quiz.get('currentStatus') === 'complete' });

    const blockQuestionsLength = quiz.get('isBlockQuiz') ? quiz.get('currentQuizBlock').get('question_ids').size : 0;
    dispatch(modalOpen((
      <SubmitModal
        totalQuestions={quiz.get('isBlockQuiz') ? blockQuestionsLength : quizRef.current.get('total_questions')}
        totalAnswered={quiz.get('answeredCount')}
      />
    ), {
      title: 'Submit for grading?',
      actionButtons: <Button onClick={() => onFinishSubmit()}>Submit</Button>,
      closeLabel: 'Cancel'
    }));
  };

  const onClickPause = quiz.get('currentStatus') !== 'incomplete' ? null : async () => {
    pause();
  };

  const onClickPrevious = () => {
    if (filter === "marked" || filter === "unanswered") {
      if (questionNumber <= 1) {
        dispatch(modalOpen(`You have reached the first question of ${filter} items`));
        return;
      }
    }

    if (quiz.get('currentStatus') !== 'complete') {
      syncResponse({ navigatingAway: true, isComplete: quiz.get('currentStatus') === 'complete' });
    }

    const prevQuestionNum = questionNumber - 1;

    // Allow instant nav for Study Mode
    if (['search', 'review'].includes(quiz.get('type'))) return goToQuestion(prevQuestionNum);

    // Check to make sure that previous question isn't part of a group
    const prevQuestionId = quiz.getIn(['question_ids', prevQuestionNum - 1]);
    const prevQuestion = questions.get(prevQuestionId);
    const prevQuestionGroup = prevQuestion.get('group_question_ids');

    if (!prevQuestionGroup.size || hasBeenSeen(prevQuestionId)) {
      return goToQuestion(prevQuestionNum);
    }

    if (['search', 'study'].includes(quiz.get('type'))
      || ((quiz.get('type') === 'test') && quiz.get('is_ngn'))) return goToQuestion(prevQuestionNum);

    return goToLastSeenQuestionInGroup(prevQuestionGroup);
  };

  const onClickNext = async () => {
    if (filter === "marked" || filter === "unanswered") {
      if (questionNumber >= maxQuestionNumber) {
        dispatch(modalOpen(`You have reached the last question of ${filter} items`));
        return;
      }
    }

    if (quiz.get('currentStatus') !== 'complete') {
      if (quiz.get('type') === 'adaptive') {
      // CAT Mode Quiz has to wait for response to sync and fetch next Question before nav
        await syncResponse({ navigatingAway: true, isComplete: quiz.get('currentStatus') === 'complete' });
        await dispatch(quizQuestionFetchNext(quizId));
      } else {
        syncResponse({ navigatingAway: true, isComplete: quiz.get('currentStatus') === 'complete' });
      }
    }

    goToQuestion(questionNumber + 1);
  };


  const goToNextQuizBlock = async (blockNumber) => {
    dispatch(quizEndQuizBlock(quizId, blockNumber));
    dispatch(modalClose());
  };


  const onClickEndQuizBlock = async () => {
    dispatch(modalOpen(<LoadingIcon>Syncing...</LoadingIcon>, { closable: false }));
    await syncResponse({ isComplete: quiz.get('currentStatus') === 'complete' });


    const blockQuestionsLength = quiz.get('isBlockQuiz') ? quiz.get('currentQuizBlock').get('question_ids').size : 0;

    if (quiz.get('boardExamInterface') === 'fred') {
      const totalAnswered = quiz.get('answeredCount');
      const questionsLength = quiz.get('isBlockQuiz') ? quiz.get('currentQuizBlock').get('question_ids').size : quiz.get('question_ids').size;
      const unansweredItems = questionsLength - totalAnswered;
      dispatch(modalOpen((
        <FredEndQuizBlockModal
          quiz={quiz}
          closeModal={() => dispatch(modalClose())}
          onFinishSubmit={onFinishSubmit}
          goToNextQuizBlock={goToNextQuizBlock}
          unansweredItems={unansweredItems}
        />
      ), {
        title: `Warning - ${unansweredItems > 0 ? 'This block is incomplete' : 'You are about to end the block!'}`,
        headerClasses: `fred-end-block bg-${unansweredItems > 0 ? 'red' : 'blue'}`,
        closable: false
      }));
    } else if (quiz.get('boardExamInterface') === 'pearson-vue') {
      dispatch(modalOpen((
        <PearsonEndQuizBlockModal
          quiz={quiz}
          closeModal={() => dispatch(modalClose())}
          goToNextQuizBlock={goToNextQuizBlock}
        />
      ), {
        closable: false,
        padding: 0,
        size: 'large'
      }));
    } else {
      dispatch(modalOpen((
        <EndQuizBlockModal
          quizType={quiz.get('type')}
          blockQuestionsLength={blockQuestionsLength}
          totalAnswered={quiz.get('answeredCount')}
        />
      ), {
        title: 'End this block?',
        actionButtons: (
          <Button
            onClick={quiz.get('onLastQuizBlock') ? onFinishSubmit : () => goToNextQuizBlock(quiz.get('currentQuizBlock').get('position') + 1)}
          >End Block
          </Button>
        ),
        closeLabel: 'Cancel'
      }));
    }
  };

  const onClickEndBreakBlock = async () => {
    goToNextQuizBlock(quiz.get('currentQuizBlock').get('position') + 1);
  };

  const blockQuestionIds = quiz.get('currentBlockQuestionIds');
  const isQuestionBlock = quiz.get('isBlockQuiz') && !!blockQuestionIds;
  const blockQuestionNumber = isQuestionBlock ? (blockQuestionIds.findIndex(id => id === questionId) + 1) : 0;
  const isLastQuizBlockQuestion = isQuestionBlock ? blockQuestionNumber === blockQuestionIds.size : false;


  useEffect(() => {
    if (quiz.get('isBlockQuiz') && blockQuestionNumber === 0 && quiz.get('currentStatus') === 'complete') {
      const blockForReview = quiz.get('questionBlocks').find(qb => qb.get('question_ids').includes(questionId));
      dispatch(quizSetLastSeenBlock(quiz.get('id'), blockForReview.get('position')));
    }

    if (quiz.get('isPractice') && quiz.get('currentStatus') !== 'complete') {
      if (quiz.get('invalidBlock')) {
        goToNextQuizBlock(quiz.get('nextValidBlock').get('position'));
      }
      if (quiz.get('inQuestionBlock')) {
        if (!quiz.get('currentQuizBlock').get('question_ids').includes(questionId)) {
          const newQuestionId = quiz.get('currentQuizBlock').get('question_ids').first();
          const newNumber = quiz.get('question_ids').findIndex(q => q === newQuestionId);
          goToQuestion(newNumber + 1);
        }
      }
      if ((quiz.get('board_exam_interface') !== 'fred' || (quiz.get('board_exam_interface') === 'fred' && quiz.get('inQuestionBlock')))) {
        if (quiz.get('blockQuizEndOfCurrentTime') && !quiz.get('onLastQuizBlock')) {
          if (quiz.get('board_exam_interface') === 'pearson-vue') {
            dispatch(modalOpen(
              <PearsonVueConfirmationModal
                timeExpired
                onFinishSubmit={() => goToNextQuizBlock(quiz.get('currentQuizBlock').get('position') + 1)}
                quiz={quiz}
              />, {
                size: 'large',
                titleSize: 'large',
                containsForm: true,
                closable: false,
                padding: 0,
                rounded: 0,
                draggable: false,
              }
            ));
          } else {
            goToNextQuizBlock(quiz.get('currentQuizBlock').get('position') + 1);
          }
        }
      }
    }
  }, [quiz.getIn(['currentQuizBlock', 'id']), quiz.get('blockQuizEndOfCurrentTime')]);

  return {
    onClickQuestionNav,
    onClickSubmit,
    onClickPause,
    onClickPrevious,
    onClickNext,
    onClickEndQuizBlock,
    onClickEndBreakBlock,
    blockQuestionNumber,
    isLastQuizBlockQuestion
  };
};
